import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommuniquesService } from '../communiques.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

export interface Communique {
  id_press_release?: number;
  title_ln1: string;
  publish_date: string;
  reference?: string;
  press_tags?: string;
  pdf_link?: string;
  pdf_name?: string;
}

@Component({
  selector: 'app-communiques-form',
  standalone: false,
  templateUrl: './communiques-form.component.html',
  styleUrls: ['./communiques-form.component.scss']
})
export class CommuniquesFormComponent implements OnInit, OnDestroy {
  communiqueId = signal<number | null>(null);
  isLoading = signal(false);
  isFetching = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  currentPdfUrl = signal<string | null>(null);
  currentPdfName = signal<string | null>(null);
  selectedPdfFile = signal<File | null>(null);
  selectedPdfName = signal<string | null>(null);

  formData = signal<Communique>({
    title_ln1: '',
    publish_date: '',
    reference: ''
  });

  private destroy$ = new Subject<void>();

  constructor(
    private communiquesService: CommuniquesService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.communiqueId.set(params['id']);
        this.loadCommunique(params['id']);
      } else {
        this.communiqueId.set(null);
        this.resetForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCommunique(id: number): void {
    this.isFetching.set(true);
    this.errorMessage.set(null);

    this.communiquesService.getCommuniqueById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const c = response.data || response;
          this.formData.set({
            id_press_release: c.id_press_release,
            title_ln1: c.title_ln1 || '',
            publish_date: this.formatDateForInput(c.publish_date),
            reference: c.reference || '',
            press_tags: c.press_tags || '',
          });
          this.currentPdfUrl.set(c.pdf_link || null);
          this.currentPdfName.set(c.pdf_name || null);
          this.isFetching.set(false);
        },
        error: (err) => {
          this.isFetching.set(false);
          SwalUtils.error('Erreur', err?.error?.message || 'Impossible de charger ce communiqué.');
        }
      });
  }

  private resetForm(): void {
    this.formData.set({ title_ln1: '', publish_date: '', reference: '', press_tags: '' });
    this.currentPdfUrl.set(null);
    this.currentPdfName.set(null);
    this.selectedPdfFile.set(null);
    this.selectedPdfName.set(null);
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  onPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      this.errorMessage.set('Seuls les fichiers PDF sont acceptés');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage.set('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    this.selectedPdfFile.set(file);
    this.selectedPdfName.set(file.name);
    this.errorMessage.set(null);
  }

  removePdf(): void {
    this.selectedPdfFile.set(null);
    this.selectedPdfName.set(null);
  }

  async onSave(): Promise<void> {
    if (!this.formData().title_ln1.trim()) {
      this.errorMessage.set('Le titre est requis');
      return;
    }
    if (!this.formData().publish_date) {
      this.errorMessage.set('La date de publication est requise');
      return;
    }

    const confirmed = await SwalUtils.confirmGreen(
      this.isEditMode ? 'Modifier le communiqué' : 'Créer le communiqué',
      this.isEditMode
        ? 'Voulez-vous enregistrer les modifications de ce communiqué ?'
        : 'Voulez-vous créer ce communiqué ?',
      this.isEditMode ? 'Modifier' : 'Créer'
    );
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    const form = new FormData();
    form.append('title_ln1', this.formData().title_ln1);
    form.append('publish_date', this.formData().publish_date);
    form.append('reference', this.formData().reference || '');
    if (this.formData().press_tags) form.append('press_tags', this.formData().press_tags!);
    if (this.selectedPdfFile()) {
      form.append('pdf', this.selectedPdfFile()!);
    }

    const isUpdate = this.communiqueId() !== null;

    if (isUpdate) {
      this.communiquesService.updateCommunique(this.communiqueId()!, form)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isLoading.set(false);
            if (res?.data?.pdf_link) this.currentPdfUrl.set(res.data.pdf_link);
            if (res?.data?.pdf_name) this.currentPdfName.set(res.data.pdf_name);
            this.selectedPdfFile.set(null);
            this.selectedPdfName.set(null);
            SwalUtils.success('Communiqué modifié', 'Les modifications ont été enregistrées avec succès.');
            this.router.navigate(['/federation/communiques', this.communiqueId()]);
          },
          error: (err) => {
            this.isLoading.set(false);
            SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de la modification.');
          }
        });
    } else {
      this.communiquesService.createCommunique(form)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isLoading.set(false);
            SwalUtils.success('Communiqué créé', 'Le communiqué a été créé avec succès.');
            if (res?.data?.id_press_release) {
              this.router.navigate(['/federation/communiques', res.data.id_press_release]);
            }
          },
          error: (err) => {
            this.isLoading.set(false);
            SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de la création.');
          }
        });
    }
  }

  onCancel(): void {
    this.router.navigate(['/federation/communiques']);
  }

  updateField(field: keyof Communique, value: any): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  private formatDateForInput(dateStr: string): string {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; }
    catch { return dateStr; }
  }

  get isEditMode(): boolean { return this.communiqueId() !== null; }
  get pageTitle(): string { return this.isEditMode ? 'Modifier le communiqué' : 'Créer un communiqué'; }
}
